import { Container } from '@/components/Container'
import { ContentHead } from '@/components/ContentHead'
import { TobiIntegration } from '@/components/tobi-integration'
import { caseContentBlock, ContentBlock } from '@/domain/content-block'
import { PopularVoteDocument } from '@/domain/documents'
import { Locale } from '@/domain/intl'
import { optionMultiLocaleString, pickMultiLocaleString } from '@/domain/multi-locale-string'
import * as t from 'io-ts'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from 'styled-components/macro'
import { NewsSection } from '../../compositions/news-section/news-section'
import { useEnv } from '../../lib'
import { UserFeedback } from '../user-feedback'
import { Tiles } from './internal'
import { InfoBox } from '@/components/InfoBox'
import * as F from 'fp-ts'

const Root = React.Fragment

interface Props extends React.ComponentPropsWithoutRef<typeof Root> {
  popularVoteId: string
  popularVoteDocument: PopularVoteDocument
  federalBallots: Array<{ id: string; name: string; hasFinalResult: boolean }>
}

function PopularVoteContainer(props: Props) {
  const { intl } = useEnv()

  const { popularVoteId, popularVoteDocument, federalBallots, ...rest } = props

  const showInfoBox = !federalBallots.every(({ hasFinalResult }) => hasFinalResult)

  const contentBlocks = F.function.pipe(
    t.array(ContentBlock).decode(
      pickMultiLocaleString(intl, popularVoteDocument.contentBlocks, '')
        .split(',')
        .map((x) => x.trim()),
    ),
    F.either.getOrElseW(() => []),
  )

  return (
    <Root {...rest}>
      <Container maxWidth={8} css={styles.contentHead as any}>
        <ContentHead
          header={
            <FormattedMessage
              id="components.PopularVoteContainer.contentHead.header"
              defaultMessage="Nationale Vorlagen"
            />
          }
          text={pickMultiLocaleString(intl, popularVoteDocument.name, '<title>')}
          lead={F.option.toNullable(optionMultiLocaleString(intl, popularVoteDocument.lead))}
        />
      </Container>

      <Container maxWidth={4} css={styles.tobi as any}>
        <TobiIntegration popularVoteId={popularVoteId} />
      </Container>

      <Container css={styles.tiles as any}>
        <Tiles popularVoteId={popularVoteId} federalBallots={federalBallots} />
      </Container>

      {showInfoBox && (
        <Container maxWidth={4} css={styles.userFeedback as any}>
           <InfoBox />
        </Container>
      )}
      <Container maxWidth={4} css={styles.userFeedback as any}>
        <UserFeedback locale={intl.locale.split('-')[0] as Locale} />
      </Container>

      {contentBlocks.map((contentBlock, i) => {
        return (
          <Container key={i} css={styles.contentBlock as any}>
            {caseContentBlock<() => React.ReactNode>({
              articles: () => {
                const articleIds = pickMultiLocaleString(intl, popularVoteDocument.articleIds, '')
                  .split(',')
                  .map((x) => x.trim())
                return <NewsSection articleIds={articleIds} />
              },
              survey: () => null,
            })(contentBlock)()}
          </Container>
        )
      })}
    </Root>
  )
}

const styles = {
  root: css``,

  contentHead: css`
    margin-bottom: 80px;
  `,
  tobi: css`
    margin-bottom: 80px;
  `,
  tiles: css`
    margin-bottom: 80px;
  `,
  userFeedback: css`
    margin-bottom: 80px;
    .stars .label.average {
      display: block !important;
    }
  `,

  contentBlock: css`
    margin-bottom: 80px;
  `,
}

export default PopularVoteContainer
